import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, message } from "antd";
import {
    CheckOutlined,
    DownloadOutlined,
    LeftOutlined,
    PlusOutlined,
    RightOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import { getFileDownloadUrl } from "../../utils/deliveryApi";
import { usePreviewNavigation } from "../../hooks/usePreviewNavigation";
import { usePreviewViewportZoom } from "../../hooks/usePreviewViewportZoom";
import { useTranslation } from "../../i18n";

// 选片图片预览（独立于 delivery）：放大查看 + 下载 + 加入/移出选片车。
// 复用 delivery 的预览样式类（与功能无关的视觉），逻辑独立。
// files + onNavigate 用于支持左右切换查看上一张/下一张照片。
function SelectionPhotoPreview({
    file,
    open,
    picked,
    note = "",
    onClose,
    onDownload,
    onTogglePick,
    onNoteChange,
    files = [],
    onNavigate,
    readOnly = false,
}) {
    const { t } = useTranslation();
    const url = file ? getFileDownloadUrl(file) : null;
    usePreviewViewportZoom(open);
    const noteEnabled = !readOnly && typeof onNoteChange === "function";
    const showDownload = typeof onDownload === "function";

    const [noteDraft, setNoteDraft] = useState(note || "");
    useEffect(() => {
        setNoteDraft(note || "");
    }, [file?.id, note]);

    const noteDirty = noteDraft.trim() !== (note || "").trim();

    // 用 ref 记录当前草稿/对应照片/原始备注，供关闭、切换照片时读取最新值，避免闭包过期。
    const draftRef = useRef(noteDraft);
    const fileRef = useRef(file);
    const noteRef = useRef(note);
    useEffect(() => {
        draftRef.current = noteDraft;
    }, [noteDraft]);
    useEffect(() => {
        fileRef.current = file;
        noteRef.current = note;
    }, [file, note]);

    // 若草稿有改动则保存并提示；用于手动保存、关闭、切换照片时的自动保存。
    const flushNote = useCallback(() => {
        if (!noteEnabled || !fileRef.current) {
            return;
        }
        const draft = (draftRef.current || "").trim();
        const original = (noteRef.current || "").trim();
        if (draft === original) {
            return;
        }
        onNoteChange(fileRef.current, draft);
        // 仅对这两条提示下移到屏幕中部（marginTop 只作用于本条 message，不影响 delivery 等其它提示）。
        message.success({
            content: t("selection.gallery.noteSaved"),
            style: { marginTop: "38vh" },
        });
        // 额外提醒：别忘了提交选片结果。第二条紧随其后堆叠显示，使用默认时长。
        message.info(t("selection.gallery.noteSubmitReminder"));
    }, [noteEnabled, onNoteChange, t]);

    const handleSaveNote = useCallback(() => {
        flushNote();
    }, [flushNote]);

    const currentIndex = useMemo(
        () => (file ? files.findIndex((entry) => entry.id === file.id) : -1),
        [file, files]
    );
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < files.length - 1;

    // 关闭预览前自动保存草稿（点击遮罩 / 关闭按钮 / Esc 都会走这里）。
    const handleClose = useCallback(() => {
        flushNote();
        onClose();
    }, [flushNote, onClose]);

    const goPrev = useCallback(() => {
        if (hasPrev) {
            flushNote();
            onNavigate?.(files[currentIndex - 1]);
        }
    }, [hasPrev, flushNote, onNavigate, files, currentIndex]);

    const goNext = useCallback(() => {
        if (hasNext) {
            flushNote();
            onNavigate?.(files[currentIndex + 1]);
        }
    }, [hasNext, flushNote, onNavigate, files, currentIndex]);

    const { swipeHandlers } = usePreviewNavigation({
        open,
        enabled: Boolean(onNavigate) && files.length > 1,
        onPrev: goPrev,
        onNext: goNext,
    });

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        },
        [handleClose]
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        document.addEventListener("keydown", handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [handleKeyDown, open]);

    if (!open || !file) {
        return null;
    }

    return (
        <div
            className="delivery-photo-preview"
            role="dialog"
            aria-modal="true"
            aria-label={file.name}
        >
            <button
                type="button"
                className="delivery-photo-preview__backdrop"
                onClick={handleClose}
                aria-label={t("selection.gallery.close")}
            />
            <div className="delivery-photo-preview__panel">
                <button
                    type="button"
                    className="delivery-photo-preview__close"
                    onClick={handleClose}
                    aria-label={t("selection.gallery.close")}
                >
                    ×
                </button>
                <div className="delivery-photo-preview__image-wrap">
                    {hasPrev && (
                        <button
                            type="button"
                            className="delivery-photo-preview__nav delivery-photo-preview__nav--prev"
                            onClick={goPrev}
                            aria-label={t("selection.gallery.previous")}
                        >
                            <LeftOutlined />
                        </button>
                    )}
                    {hasNext && (
                        <button
                            type="button"
                            className="delivery-photo-preview__nav delivery-photo-preview__nav--next"
                            onClick={goNext}
                            aria-label={t("selection.gallery.next")}
                        >
                            <RightOutlined />
                        </button>
                    )}
                    {url && (
                        <img
                            key={file.id}
                            src={url}
                            alt=""
                            draggable={false}
                            className="delivery-photo-preview__image"
                            {...swipeHandlers}
                        />
                    )}
                </div>
                <p className="delivery-photo-preview__name">{file.name}</p>
                {!readOnly && (
                    <div className="delivery-photo-preview__actions selection-preview__actions">
                        <Button
                            type={picked ? "default" : "primary"}
                            icon={picked ? <CheckOutlined /> : <PlusOutlined />}
                            onClick={() => onTogglePick(file)}
                        >
                            {picked
                                ? t("selection.gallery.removePick")
                                : t("selection.gallery.addPick")}
                        </Button>
                        {showDownload && (
                            <Button icon={<DownloadOutlined />} onClick={() => onDownload(file)}>
                                {t("selection.gallery.download")}
                            </Button>
                        )}
                    </div>
                )}
                {readOnly && showDownload && (
                    <div className="delivery-photo-preview__actions selection-preview__actions">
                        <Button icon={<DownloadOutlined />} onClick={() => onDownload(file)}>
                            {t("selection.gallery.download")}
                        </Button>
                    </div>
                )}
                {readOnly && note ? (
                    <div className="selection-preview__note selection-preview__note--readonly">
                        <p className="selection-preview__note-label">{t("selection.gallery.noteLabel")}</p>
                        <p className="selection-preview__note-readonly">{note}</p>
                    </div>
                ) : null}
                {noteEnabled && (
                    <div className="selection-preview__note">
                        <label className="selection-preview__note-label" htmlFor="selection-note-input">
                            {t("selection.gallery.noteLabel")}
                        </label>
                        <Input.TextArea
                            id="selection-note-input"
                            value={noteDraft}
                            onChange={(event) => setNoteDraft(event.target.value)}
                            placeholder={t("selection.gallery.notePlaceholder")}
                            autoSize={{ minRows: 2, maxRows: 5 }}
                            maxLength={2000}
                        />
                        <div className="selection-preview__note-footer">
                            <p className="selection-preview__note-hint">
                                {t("selection.gallery.noteHint")}
                            </p>
                            <Button
                                type="primary"
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={handleSaveNote}
                                disabled={!noteDirty}
                            >
                                {t("selection.gallery.noteSave")}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SelectionPhotoPreview;
